import { useEffect, useState } from 'react';
import { ACTION_TYPES, useParlamentarianSearchDispatch, useParlamentarianSearchState } from './parlamentarian-search-context';
import ParlamentarianAppService from '../../../../application/platform/sge/parlamentarian/parlamentarian-app-service';
import PoliticalPartyAppService from '../../../../application/platform/sge/political-party/political-party-app-service';
import { useFeedback } from '../../../../hooks/use-feedback';
import { IParlamentarianAddEditRequest } from '../../../../model/sge/parlamentarian/request/parlamentarian-add-edit-request';
import { IParlamentarianPagedResponse } from '../../../../model/sge/parlamentarian/response/parlamentarian-paged-response';
import { parseNumberInput } from '../../../../utils/format';

export const useParlamentarianSearchController = () => {
    const dispatch = useParlamentarianSearchDispatch();
    const state = useParlamentarianSearchState();
    const { showSuccess, showError, confirmDelete, withLoading } = useFeedback();
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        PoliticalPartyAppService.listAll()
            .then((parties) => dispatch({ type: ACTION_TYPES.setPoliticalParties, payload: parties }))
            .catch((error: any) => showError('Não foi possível carregar os partidos políticos', error));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Filtros
    function handleOnChange(e: any) {
        const { name, value } = e.target;
        if (name === 'input-name') {
            dispatch({ type: ACTION_TYPES.updateParameter, payload: { name: value || undefined } });
        } else if (name === 'select-type') {
            dispatch({ type: ACTION_TYPES.updateParameter, payload: { type: parseNumberInput(value) } });
        }
    }

    function handleSearch() {
        dispatch({ type: ACTION_TYPES.updateParameter, payload: { page: 0 } });
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    function handleClearFilters() {
        dispatch({ type: ACTION_TYPES.updateParameter, payload: { page: 0, name: undefined, type: undefined } });
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    function handlePaginationChange(page: number, pageSize: number) {
        dispatch({ type: ACTION_TYPES.updateParameter, payload: { page, pageSize } });
        dispatch({ type: ACTION_TYPES.triggerSearch });
    }

    // Cadastro/edição: a listagem não traz os contatos, então a edição busca o registro completo
    async function openDrawer(row?: IParlamentarianPagedResponse) {
        if (!row) {
            dispatch({ type: ACTION_TYPES.openDrawer });
            return;
        }
        try {
            const parlamentarian = await withLoading(() => ParlamentarianAppService.getById(row.id));
            dispatch({ type: ACTION_TYPES.openDrawer, payload: parlamentarian });
        } catch (error: any) {
            showError('Não foi possível carregar o parlamentar', error);
        }
    }

    function closeDrawer() {
        dispatch({ type: ACTION_TYPES.closeDrawer });
    }

    async function handleSave(request: IParlamentarianAddEditRequest) {
        const editing = state.drawer.record;
        setIsSaving(true);
        try {
            if (editing) {
                await ParlamentarianAppService.edit(editing.id, request);
                showSuccess('Parlamentar atualizado com sucesso!');
            } else {
                await ParlamentarianAppService.add(request);
                showSuccess('Parlamentar adicionado com sucesso!');
            }
            closeDrawer();
            dispatch({ type: ACTION_TYPES.triggerSearch });
        } catch (error: any) {
            showError('Não foi possível salvar o parlamentar', error);
        } finally {
            setIsSaving(false);
        }
    }

    async function removeParlamentarian(row: IParlamentarianPagedResponse) {
        try {
            await ParlamentarianAppService.remove(row.id);
            showSuccess('Parlamentar excluído com sucesso!');
            dispatch({ type: ACTION_TYPES.triggerSearch });
        } catch (error: any) {
            showError('Não foi possível excluir o parlamentar', error);
        }
    }

    function handleDelete(row: IParlamentarianPagedResponse) {
        confirmDelete(`Tem certeza que deseja excluir o parlamentar "${row.personName || ''}"?`, () => { removeParlamentarian(row); });
    }

    return { isSaving, handleOnChange, handleSearch, handleClearFilters, handlePaginationChange, openDrawer, closeDrawer, handleSave, handleDelete };
};
